import type { EducationType } from '@hygge/shared';
import type { Education, EducationInput } from '@/lib/employee-finance';

/**
 * The Education dialog's rules [HW-1008], out of the component for the node
 * vitest (see profile-tabs.ts). Messages repeat CreateEducationDto's.
 */

export interface EducationForm {
  type: EducationType;
  institution: string;
  specialty: string;
  /** YYYY-MM-DD or '' */
  startDate: string;
  /** YYYY-MM-DD or '' — empty means still studying */
  endDate: string;
}

export type EducationErrors = Partial<Record<keyof EducationForm, string>>;

// The API's limits (CreateEducationDto), checked on the trimmed text it stores.
const MAX_TEXT = 300;

/**
 * A calendar day the API's strict ISO 8601 check takes: YYYY-MM-DD that
 * exists (no 2021-02-29), and — as class-validator's isISO8601 answers — a
 * year from 1000 on, so a mistyped 0999 is caught here rather than by a 400.
 */
export function isApiCalendarDay(value: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (year < 1000 || month < 1 || month > 12 || day < 1) return false;
  return day <= new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Date fields the browser could not read: a native date input reports '' for
 * a partly typed or impossible date (31 Feb) — the same as a cleared field —
 * and only its `validity.badInput` tells them apart. Without this the entry
 * would save as "still studying" or with no start. [HW-1008]
 */
export type UnreadableDates = Partial<Record<'startDate' | 'endDate', boolean>>;

export function validateEducation(
  form: EducationForm,
  unreadable: UnreadableDates = {}
): EducationErrors {
  const errors: EducationErrors = {};
  const institution = form.institution.trim();
  if (!institution) errors.institution = 'Institution is required';
  else if (institution.length > MAX_TEXT) {
    errors.institution = `Institution must be at most ${MAX_TEXT} characters`;
  }
  if (form.specialty.trim().length > MAX_TEXT) {
    errors.specialty = `Specialty must be at most ${MAX_TEXT} characters`;
  }
  if (form.startDate && !isApiCalendarDay(form.startDate)) {
    errors.startDate = 'Start date is not a real calendar day';
  }
  if (form.endDate && !isApiCalendarDay(form.endDate)) {
    errors.endDate = 'End date is not a real calendar day';
  }
  if (unreadable.startDate) errors.startDate = 'Enter a valid date';
  if (unreadable.endDate) errors.endDate = 'Enter a valid date';
  // YYYY-MM-DD compares correctly as text.
  if (!errors.startDate && !errors.endDate && form.startDate && form.endDate) {
    if (form.endDate < form.startDate) errors.endDate = 'End date must not be before start date';
  }
  return errors;
}

/** How a server 400 names each field — see PAYMENT_DETAILS_SERVER_FIELDS. */
export const EDUCATION_SERVER_FIELDS: Record<keyof EducationForm, readonly string[]> = {
  type: ['type '],
  institution: ['institution ', 'Institution '],
  specialty: ['specialty ', 'Specialty '],
  startDate: ['startDate ', 'Start date '],
  endDate: ['endDate ', 'End date '],
};

export function toEducationInput(form: EducationForm): EducationInput {
  return {
    type: form.type,
    institution: form.institution.trim(),
    specialty: form.specialty.trim() || null,
    startDate: form.startDate || null,
    endDate: form.endDate || null,
  };
}

/** A new entry opens as Bachelor, the most common answer. */
export function toEducationForm(entry: Education | null | undefined): EducationForm {
  return {
    type: entry?.type ?? 'bachelor',
    institution: entry?.institution ?? '',
    specialty: entry?.specialty ?? '',
    startDate: entry?.startDate ?? '',
    endDate: entry?.endDate ?? '',
  };
}
